import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import {
  put,
  select,
  takeEvery,
  delay,
  race,
  call,
  take,
  cancel as cancelSaga,
  cancelled,
} from "redux-saga/effects";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { ChatFormTypes } from "../../../../Components/Result/ChatForm/ChatForm";
import { getUserChatsActions } from "../getUserChats/getUserChats";
import { GetUserChatsDataTypes } from "../../../../Schema/getUserChats.types";
import i18next from "../../../../i18next";

import {
  resetStreamingChatRes,
  setError,
  setInitLoading,
  setPreventToGetChats,
  setResetResources,
  setResources,
  setSessionId,
  setStreamedChat,
  setStreamingChatId,
  setStreamingChatRes,
  setStreamingUserChatMessage,
  setStatusStates,
  setImage,
  StreamImageTypes,
  StatusStreamTypes,
  setNeedAccountUpgrade,
  setSentFiles,
  setResetFiles,
  setGeneratedFiles,
  GeneratedFilesTypes,
  GeneratedVideoTypes,
  GeneratedAudioTypes,
  setGeneratedVideo,
  setGeneratedAudio, setSessionEditedId,
  truncateStreamedChatsAfter,
} from "../../streamedChat/streamedChat";
import { v4 as uuid } from "uuid";
import { setShouldScrollToEnd } from "../../subService/subServiceChats";
import {
  removeMetaDataFromRes,
  extractSessionId,
  extractSessionEditedId,
  extractRefrences,
  extractStatus,
  extractImageUrl,
  extractRevisedPrompt,
  extractNeedAccountUpgrade,
  extractFileUrl,
  extractVideoUrl, extractAudioUrl,
} from "../../../../Utils/stringProcessor";
import { resetFiles } from "../../files/uploadedFiles/uploadedFiles";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { streamStatusUtil } from "../../../../Utils/streamStatusUtil";
import i18n from "../../../../i18next";
import { resetFileType } from "../../selectedFileType/selectedFileType";
import {ChatStorageService} from "../../../../Hooks/useChatStorageService";

export type PostThirdPartyUserServiceChatLoadAction = {
  type: string;
  payload: ChatFormTypes;
  isAgency?: boolean;
};

export type PostThirdPartyUserServiceChatData = {
  data: {
    data: GetUserChatsDataTypes;
    message_count: number;
    message_limit: number;
  };
};

let streamController: AbortController | null = null;

const PostThirdPartyUserServiceChat = new ReduxFetchState<
  PostThirdPartyUserServiceChatData | null,
  ChatFormTypes,
  any
>("postThirdPartyUserServiceChat");

export const CANCEL_STREAM = "postThirdPartyUserServiceChat/cancel";

export function* watchPostThirdPartyUserServiceChat(
  action: PostThirdPartyUserServiceChatLoadAction,
) {
  try {
    streamController = new AbortController();

    const { id, file_ids, question, reset, fileLinks, setAudioFile, setAudioURL, isAgency, edited_id,setValue,messageEditId } = action.payload;
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const toolName: string = yield select((state) => state.selectedFileType?.fileType?.prompt);

    yield put(setShouldScrollToEnd(true));

    const formData = new FormData();
    if (file_ids?.length) {
      file_ids?.forEach((fileId, index) => formData.append(`file_ids[${index}]`, String(fileId)));
    }
    if (question) {
      formData.append("question", question);
    }
    if (edited_id != null) {
      formData.append("edited_id", String(edited_id));
    }
    if (toolName) {
      formData.append("forced_tool_name", toolName);
    } else {
      formData.append("forced_tool_name", "auto");
    }
    reset && reset();
    yield put(resetFileType());
    yield put(resetFiles());
    yield put(resetStreamingChatRes());
    yield put(setResetResources());
    yield put(setStreamingChatId(uuid()));
    yield put(setSessionId(""));
    yield put(setSessionEditedId(edited_id != null ? String(edited_id) : ""));
    if (edited_id != null) {
      yield put(truncateStreamedChatsAfter(Number(edited_id)));
    }
    yield put(setError({ errorMessage: "", retry: false }));
    yield put(setImage(null));
    yield put(setResetFiles());
    yield put(setGeneratedFiles(null));
    yield put(setGeneratedVideo(null));
    yield put(setGeneratedAudio(null));
    setAudioFile && setAudioFile(null);
    setAudioURL && setAudioURL(null);
    yield put(setStreamingUserChatMessage(question || ""));

    if (fileLinks?.length) {
      yield put(setSentFiles(fileLinks));
    } else {
      yield put(setResetFiles());
    }

    yield put(setInitLoading(true));

    const { response, cancel } = yield race({
      response: call(fetch, `https://api.lamtakam.com/api/v1/ai/${isAgency ? "agency" : "third-party"}/${id}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { lang },
        body: formData,
        signal: streamController.signal,
      }),
      cancel: take(CANCEL_STREAM),
    });

    if (cancel) {
      streamController.abort();
      yield put(setInitLoading(false));
      yield put(setStatusStates({ label: "", status: "" }));
      yield cancelSaga();
      return;
    }

    if (!response || !response?.ok) {
      const error: { error: string; retry: boolean } = yield response.json();
      if (response.status === 400){
        ChatStorageService.savePendingChat({
          id: action.payload.id,
          question: action.payload.question,
          file_ids: action.payload.file_ids,
          fileLinks: action.payload.fileLinks,
        })
      }
      yield put(setError({ errorMessage: error.error, retry: error.retry }));
      yield put(PostThirdPartyUserServiceChat.actions.loadFailure(error.error));
      yield put(setInitLoading(false));
      return;
    }
    if (setValue){
      setValue("messageId", messageEditId, { shouldDirty: true });
    }

    ChatStorageService.clearPendingChat();
    const reader = response.body?.getReader();
    const decoder = new TextDecoder("utf-8");

    let buffer = "";
    let resources: any = "";
    let sessionId = "";
    let sessionEditedId = edited_id != null ? String(edited_id) : "";
    let image: StreamImageTypes | null = null;
    let needAccountUpgrade: string = "";
    let generatedFiles: GeneratedFilesTypes[] | null = null;
    let generatedVideo: GeneratedVideoTypes[] | null = null;
    let generatedAudio: GeneratedAudioTypes[] | null = null;

    while (true) {
      //@ts-ignore
      if (yield cancelled()) {
        return;
      }

      const { result, cancel } = yield race({
        result: call([reader, reader.read]),
        cancel: take(CANCEL_STREAM),
      });

      if (cancel) {
        streamController.abort();
        yield put(setInitLoading(false));
        yield put(setStatusStates({ label: "", status: "" }));
        yield cancelSaga();
        return;
      }

      const { value, done } = result;

      if (done) {
        const chatId: string = yield select((state) => state.streamedChat.streamingChatId);

        if (image) yield put(setImage(image));
        if (resources) yield put(setResources(resources));
        if (sessionId) yield put(setSessionId(sessionId));
        if (sessionEditedId) yield put(setSessionEditedId(sessionEditedId));
        if (needAccountUpgrade) yield put(setNeedAccountUpgrade(needAccountUpgrade));

        yield put(
          setStreamedChat({
            id: chatId,
            message: question || "",
            res: buffer,
            resources,
            sessionId,
            sessionEditedId,
            image,
            sentFiles: fileLinks,
            generatedFiles,
            generatedVideo,
            generatedAudio,
          }),
        );
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      buffer = buffer.toString();

      yield put(setInitLoading(false));
      yield put(setStreamingChatRes(chunk));

      resources = extractRefrences(buffer);

      const extractedSessionId = extractSessionId(buffer);
      if (extractedSessionId != null) {
        sessionId = String(extractedSessionId);
        yield put(setSessionId(sessionId));
      }

      const rawSessionEditedId = extractSessionEditedId(buffer);
      if (rawSessionEditedId != null) {
        sessionEditedId = String(rawSessionEditedId);
        yield put(setSessionEditedId(sessionEditedId));
      } else if (edited_id != null && !sessionEditedId) {
        sessionEditedId = String(edited_id);
      }

      needAccountUpgrade = extractNeedAccountUpgrade(buffer).toString();

      const status = extractStatus(buffer).at(-1) || "";
      yield streamStatusUtil(status, function* (payload) {
        yield put(setStatusStates(payload));
      });

      const imageUrls = extractImageUrl(buffer);
      const revisedPrompt = extractRevisedPrompt(buffer);
      generatedFiles = extractFileUrl(buffer);
      generatedAudio = extractAudioUrl(buffer);
      generatedVideo = extractVideoUrl(buffer);

      image = { url: imageUrls, revisedPrompt };

      if (imageUrls.length) yield put(setImage(image));
      if (generatedFiles.length) yield put(setGeneratedFiles(generatedFiles));
      if (generatedVideo.length) yield put(setGeneratedVideo(generatedVideo));
      if (generatedAudio.length) yield put(setGeneratedAudio(generatedAudio));

      if (removeMetaDataFromRes(buffer).length) {
        yield put(setStatusStates({ label: "", status: "" }));
      }

      if (buffer.includes("ThirdPartyError")) {
        yield put(setStatusStates({ label: "", status: "" }));
        yield put(
          setError({
            //@ts-ignore
            errorMessage: i18next.t("result.resourceNotAvailable"),
            retry: false,
          }),
        );
      }
    }

    yield put(PostThirdPartyUserServiceChat.actions.loadSuccess(null));
    yield put(setPreventToGetChats(true));
    yield put(getUserChatsActions.loadSuccess(null));
  } catch (e: any) {
    if (e.message.includes("Failed to fetch")) {
      //@ts-ignore
      yield put(setError({ errorMessage: i18next.t("failedToFetch"), retry: true }));
    }
    yield put(PostThirdPartyUserServiceChat.actions.loadFailure(e.message));
  } finally {
    yield put(setInitLoading(false));
  }
}

export function* postThirdPartyUserServiceChatSagas() {
  yield takeEvery(
    PostThirdPartyUserServiceChat.actionTypes.load,
    watchPostThirdPartyUserServiceChat,
  );
}

export function usePostThirdPartyUserServiceChat() {
  const { loading, data, ...postThirdPartyUserServiceChatState } = useAppSelector(
    (state) => state.postThirdPartyUserServiceChat,
  );
  const dispatch = useAppDispatch();

  const dispatchPostThirdPartyUserServiceChat = useCallback(
    (data: ChatFormTypes) => {
      dispatch(PostThirdPartyUserServiceChat.actions.load(data));
    },
    [dispatch],
  );

  const dispatchCancelStream = useCallback(() => {
    dispatch({ type: CANCEL_STREAM });
    dispatch(postThirdPartyUserServiceChatActions.loadSuccess(null));
  }, [dispatch]);

  return {
    ...postThirdPartyUserServiceChatState,
    dispatchPostThirdPartyUserServiceChat,
    postThirdPartyUserServiceChatLoading: loading,
    postThirdPartyUserServiceChatData: data,
    dispatchCancelStream,
  };
}

export const {
  actionTypes: postThirdPartyUserServiceChatActionsTypes,
  actions: postThirdPartyUserServiceChatActions,
  reducer: postThirdPartyUserServiceChatReducer,
} = PostThirdPartyUserServiceChat;
